import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { DollarSign, MousePointerClick, TrendingUp, Wallet, ArrowRight, Sparkles } from "lucide-react";

function Stat({ label, value, prefix, icon: Icon, tint }) {
  return (
    <div className={`pf-card p-5 flex items-start justify-between ${tint || ""}`}>
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <div className="font-display text-3xl font-medium tracking-tight mt-2 text-slate-900 tabular-nums">
          {prefix}{value}
        </div>
      </div>
      <Icon className="w-5 h-5 text-cyan-600" />
    </div>
  );
}

export default function AffiliateOverview({ profile }) {
  const [dash, setDash] = useState(null);

  useEffect(() => { api.get("/affiliate/dashboard").then((r) => setDash(r.data)); }, []);

  if (!dash) return <div className="text-slate-500">Loading…</div>;
  const buckets = dash.buckets || {};
  const accrued = buckets.accrued || { total: 0, count: 0 };
  const paid = buckets.paid || { total: 0, count: 0 };

  return (
    <div className="space-y-6" data-testid="affiliate-overview">
      <div>
        <div className="pf-chip pf-chip-cyan"><Sparkles className="w-3 h-3" /> Affiliate</div>
        <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 text-slate-900">
          Welcome, <span className="pf-gradient-text-2">{dash.affiliate.user_name}</span>
        </h2>
        <p className="text-sm text-slate-600 mt-1">
          Your tracking slug: <code className="font-mono px-2 py-0.5 bg-white/70 rounded-md text-cyan-700">{dash.affiliate.slug}</code>
        </p>
      </div>

      <div className="grid md:grid-cols-4 gap-3">
        <Stat label="Total earned" value={dash.affiliate.total_earned.toFixed(2)} prefix="$" icon={DollarSign} />
        <Stat label="Pending" value={accrued.total.toFixed(2)} prefix="$" icon={TrendingUp} />
        <Stat label="Paid out" value={paid.total.toFixed(2)} prefix="$" icon={Wallet} />
        <Stat label="Clicks" value={dash.affiliate.click_count} prefix="" icon={MousePointerClick} />
      </div>

      <div className="pf-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Recent conversions</div>
            <div className="font-display text-xl mt-1 text-slate-900">{dash.affiliate.conversion_count} orders attributed to you</div>
          </div>
          <Link to="/affiliate/earnings" className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1" data-testid="aff-view-all-earnings">
            View ledger <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        {dash.recent_conversions.length === 0 ? (
          <div className="text-slate-500 text-sm py-8 text-center">
            No conversions yet. Grab a tracking link and share it.
            <div className="mt-3">
              <Link to="/affiliate/links" className="pf-btn-primary px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1" data-testid="aff-cta-links">
                Get tracking links <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-widest text-slate-500">
              <tr><th className="py-2">Campaign</th><th className="py-2">Revenue</th><th className="py-2">Commission</th><th className="py-2">Status</th><th className="py-2">When</th></tr>
            </thead>
            <tbody>
              {dash.recent_conversions.map((c) => (
                <tr key={c.id} className="border-t border-white/60">
                  <td className="py-2 font-medium">{c.campaign_name}</td>
                  <td className="py-2 tabular-nums">${c.revenue.toFixed(2)}</td>
                  <td className="py-2 tabular-nums font-semibold text-cyan-700">+${c.commission_amount.toFixed(2)}</td>
                  <td className="py-2"><span className={`pf-chip ${c.status === "paid" ? "pf-chip-mint" : ""}`}>{c.status}</span></td>
                  <td className="py-2 text-xs text-slate-500">{new Date(c.accrued_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
